import { Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LegalPageLayout, {
  LegalSection as H2,
  LegalParagraph as P,
} from '@components/legal/LegalPageLayout';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

const LAST_UPDATED = '27 October 2026';

/**
 * The published subprocessor list (DRAFT, REV-1120). Public route
 * /legal/subprocessors, linked from the Privacy Policy (section 4) and the DPA
 * (section 5), which used to say the list was "available on request".
 *
 * Every row is checked against the code: Anthropic through the AI proxy
 * (src/services/aiProxyService.js in revenant-api), with the organization's own
 * key, AFixt's only on a deployment that opts in (REV-1120), never with AI
 * switched off (REV-1127); Stripe for billing, from the first paid-plan checkout
 * (billingService.createCheckoutSession sends the org's name and id). The email and hosting providers
 * are not chosen yet (REV-421), and the page says so rather than guessing.
 */
function SubprocessorsPage() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout title={t('legal.subprocessors')} lastUpdated={LAST_UPDATED}>
      <P>
        These are the third parties that may process personal data or customer content on our behalf
        to provide Revenant. Our{' '}
        <Link component={RouterLink} to="/legal/dpa">
          {t('legal.dpa')}
        </Link>{' '}
        commits us to give notice of changes to this list and an opportunity to object.
      </P>

      <ScrollableTableContainer label={t('legal.subprocessors')}>
        <Table aria-label={t('legal.subprocessors')}>
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col">
                Subprocessor
              </TableCell>
              <TableCell component="th" scope="col">
                Purpose
              </TableCell>
              <TableCell component="th" scope="col">
                Data it receives
              </TableCell>
              <TableCell component="th" scope="col">
                When
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Anthropic, PBC
              </TableCell>
              <TableCell>AI model provider for the optional AI features</TableCell>
              <TableCell>
                For a suggested fix: the finding&rsquo;s rule, the page URL, and the affected
                element&rsquo;s selector and markup as captured from the scanned page (up to 4,000
                characters). For a drafted remark: the audit&rsquo;s name and evaluation period, the
                criterion&rsquo;s test results, and up to five testers&rsquo; notes on failing
                tests.
              </TableCell>
              <TableCell>
                Only while AI is switched on for the organization, and only where AFixt&rsquo;s own
                AI key is used (see below)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Stripe, Inc.
              </TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>
                The organization&rsquo;s name and ID; payment details are entered on Stripe&rsquo;s
                own checkout page
              </TableCell>
              <TableCell>From the first checkout for a paid plan</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Email delivery provider
              </TableCell>
              <TableCell>Sending account and notification email</TableCell>
              <TableCell>Recipient email address and message content</TableCell>
              <TableCell>To be named before launch</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Hosting provider
              </TableCell>
              <TableCell>Infrastructure</TableCell>
              <TableCell>All data the Service holds</TableCell>
              <TableCell>To be named before launch</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ScrollableTableContainer>

      <H2>AI features and Anthropic</H2>
      <P>
        Revenant&rsquo;s AI features are optional: a suggested fix for one finding, and a drafted
        remark for an audit criterion, each made only when a person asks for it and reviewed by a
        person before use. Every request goes through Revenant&rsquo;s AI proxy and is recorded in
        the organization&rsquo;s audit trail. An Owner or Admin can switch every AI feature off, and
        then no request is sent.
      </P>
      <P>
        Requests are made with your organization&rsquo;s own Anthropic API key, so Anthropic
        processes them under your organization&rsquo;s own agreement with Anthropic. AFixt&rsquo;s
        own key is used only on a deployment set up for it, such as a demonstration; there,
        Anthropic acts as our subprocessor.
      </P>
      <P>
        Anthropic&rsquo;s Commercial Terms (section B, Customer Content) say Anthropic may not train
        models on customer content from its services.{' '}
        <Link href="https://www.anthropic.com/legal/commercial-terms">
          Read Anthropic&rsquo;s Commercial Terms
        </Link>
        .
      </P>
      <P>
        Anthropic&rsquo;s privacy center says it deletes API inputs and outputs within 30 days of
        receipt or generation. The exceptions it lists: different terms agreed with Anthropic (such
        as zero data retention), a service with longer retention under the customer&rsquo;s control,
        enforcing its Usage Policy, or a legal requirement.{' '}
        <Link href="https://privacy.claude.com/en/articles/7996866-how-long-do-you-store-my-organization-s-data">
          Read Anthropic&rsquo;s data retention article
        </Link>
        .
      </P>
    </LegalPageLayout>
  );
}

export default SubprocessorsPage;
